/**
 * Pencocokan nama jam untuk metrik "Users Parlent".
 *
 * Nama jam dikirim apa adanya oleh aplikasi (nama yang diiklankan perangkat
 * BLE). Daftar model yang dihitung sebagai jam Parlent disimpan di Firestore
 * (`/appSettings/current.watchModels.parlent`) supaya model baru bisa
 * ditambahkan dari dashboard tanpa merilis ulang aplikasi.
 */

/** Dipakai kalau `/appSettings/current` belum punya `watchModels`. */
export const DEFAULT_PARLENT_WATCH_MODELS: string[] = ['Parlent Active']

/**
 * Samakan bentuk nama sebelum dibandingkan: huruf kecil, spasi dirapikan.
 * Pemisah (`-`, `_`) disamakan jadi spasi supaya "Parlent_Active" tetap cocok.
 */
export function normalizeWatchName(value: string | null | undefined): string {
  if (!value) return ''
  return value
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Cocok kalau nama perangkat sama persis dengan salah satu entri whitelist,
 * atau diawali entri tersebut.
 *
 * Prefix dipakai karena jam sering menyiarkan nama bersufiks serial —
 * "Parlent Active-A3F2" harus tetap terhitung sebagai "Parlent Active".
 */
export function isParlentWatch(
  deviceName: string | null | undefined,
  whitelist: string[] = DEFAULT_PARLENT_WATCH_MODELS,
): boolean {
  const name = normalizeWatchName(deviceName)
  if (!name) return false

  return whitelist.some((entry) => {
    const target = normalizeWatchName(entry)
    if (!target) return false
    return name === target || name.startsWith(target)
  })
}

/** Label rapi untuk ditampilkan di tabel; kosong jadi "Unknown device". */
export function formatWatchLabel(deviceName: string | null | undefined): string {
  const trimmed = deviceName?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : 'Unknown device'
}

/**
 * Jam mana yang mewakili satu user di ringkasan satu-pill.
 *
 * Jam Parlent diutamakan karena itulah yang menentukan status user di metrik:
 * user dengan satu jam Parlent dan dua merek lain tetap terhitung Users
 * Parlent, jadi pill wakilnya tidak boleh menampilkan merek lain.
 */
export function pickPrimaryWatch<T extends { isParlent: boolean }>(
  watches: T[],
): T | null {
  if (watches.length === 0) return null
  return watches.find((watch) => watch.isParlent) ?? watches[0]
}
